import { useMemo } from 'react'
import { CATEGORY_LABELS, getItemDef } from '../game/items'
import { getPortraitSrc } from '../game/portraits'
import { useI18n } from '../i18n/useI18n'
import type { ItemCategory, ItemRarity, LootWeight } from '../game/types'

export type RecruitLootPreview = {
  /** Undefined when the recruit has no portrait key yet; badge is hidden. */
  portraitKey: string | undefined
  /** True when this recruit is already on the shelter roster */
  recruited: boolean
}

type Props = {
  loot: LootWeight[]
  className?: string
  obfuscated?: boolean
  recruitPreview?: RecruitLootPreview
}

const CATEGORY_SORT: ItemCategory[] = [
  'food',
  'water',
  'meds',
  'scrap',
  'weapon',
  'trinket',
]

const RARITY_SORT: Record<ItemRarity, number> = {
  common: 0,
  uncommon: 1,
  special: 2,
}

const CAT_SHORT_KEY: Record<Exclude<ItemCategory, 'trinket'>, string> = {
  food: 'cat_food_short',
  water: 'cat_water_short',
  meds: 'cat_meds_short',
  scrap: 'cat_scrap_short',
  weapon: 'cat_weapon_short',
}

type LootGroup = {
  category: ItemCategory
  /** Highest rarity among loot entries in this category */
  maxRarity: ItemRarity
}

function maxRarity(a: ItemRarity, b: ItemRarity): ItemRarity {
  return RARITY_SORT[a] >= RARITY_SORT[b] ? a : b
}

function sortLootGroups(a: LootGroup, b: LootGroup): number {
  return CATEGORY_SORT.indexOf(a.category) - CATEGORY_SORT.indexOf(b.category)
}

/** One + per tier above common (uncommon → +, special → ++). */
function rarityPlusSuffix(rarity: ItemRarity): string {
  return '+'.repeat(RARITY_SORT[rarity])
}

function categoryDisplayLabel(
  category: ItemCategory,
  t: ReturnType<typeof useI18n>['t'],
  pick: ReturnType<typeof useI18n>['pick'],
): string {
  if (category === 'trinket') return pick(CATEGORY_LABELS.trinket)
  return t(CAT_SHORT_KEY[category] as never)
}

function RecruitPortraitBadge({ recruitPreview }: { recruitPreview: RecruitLootPreview }) {
  const src = getPortraitSrc(recruitPreview.portraitKey)
  if (!src) return null

  return (
    <div className="relative h-7 w-7 shrink-0 self-center">
      <div className="h-full w-full overflow-hidden rounded-full bg-stone-900 ring-1 ring-inset ring-stone-600">
        <img
          src={src}
          alt=""
          aria-hidden
          decoding="async"
          className={`h-full w-full object-cover ${recruitPreview.recruited ? '' : 'brightness-0'}`}
        />
        {!recruitPreview.recruited ? (
          <div className="absolute inset-0 ring-1 ring-inset ring-stone-700 rounded-full">
             <span className="absolute inset-0 top-1/3 flex items-center justify-center text-[9px] font-bold leading-none text-stone-300">?</span>
          </div>
        ) : null}
      </div>
      {recruitPreview.recruited ? (
        <span
          className="absolute -right-0.5 -bottom-0.5 flex h-[10px] w-[10px] items-center justify-center rounded-full bg-emerald-600 text-[9px] font-bold leading-none text-white ring-2 ring-stone-900"
          aria-hidden
        >
          ✓
        </span>
      ) : null}
    </div>
  )
}

export function LocationLootPreview({
  loot,
  className,
  obfuscated = false,
  recruitPreview,
}: Props) {
  const { t, pick } = useI18n()

  const groups = useMemo(() => {
    const byCat = new Map<ItemCategory, ItemRarity>()
    for (const entry of loot) {
      const def = getItemDef(entry.itemId)
      if (!def) continue
      const prev = byCat.get(def.category)
      byCat.set(
        def.category,
        prev ? maxRarity(prev, def.rarity) : def.rarity,
      )
    }
    const list: LootGroup[] = CATEGORY_SORT.filter((c) => byCat.has(c)).map(
      (category) => ({
        category,
        maxRarity: byCat.get(category)!,
      }),
    )
    return list.sort(sortLootGroups)
  }, [loot])

  if (groups.length === 0 && !recruitPreview) return null

  return (
    <div className={className}>
      <div className="mb-1 text-[9px] uppercase tracking-wider text-stone-500">
        {t('exp_possible_loot')}
      </div>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          {obfuscated ? (
            <div className="inline-flex flex-wrap items-center gap-1">
              <div className="inline-flex rounded border border-stone-800/90 bg-stone-950/50 px-1.5 py-0.5 text-[10px] leading-tight text-stone-300">
                ???
              </div>
              <span className="text-[10px] text-stone-500">{t('explore_to_reveal')}</span>
            </div>
          ) : (
            <ul className="flex flex-wrap gap-x-1.5 gap-y-1">
              {groups.map((group) => {
                const label = categoryDisplayLabel(group.category, t, pick)
                return (
                  <li
                    key={group.category}
                    className="rounded border border-stone-800/90 bg-stone-950/50 px-1.5 py-0.5 text-[10px] capitalize leading-tight text-white"
                  >
                    {label}
                    {rarityPlusSuffix(group.maxRarity)}
                  </li>
                )
              })}
            </ul>
          )}
        </div>
        {recruitPreview ? <RecruitPortraitBadge recruitPreview={recruitPreview} /> : null}
      </div>
    </div>
  )
}
